import posthog from "posthog-js";
import * as Sentry from "@sentry/react";

/**
 * Centralised analytics event names. Mirror server + mobile event lists.
 */
export const EVENTS = {
  appOpen: "app_open",
  signIn: "sign_in",
  signUp: "sign_up",
  viewKundali: "view_kundali",
  computeChart: "compute_chart",
  viewDailyHoroscope: "view_daily_horoscope",
  askAi: "ask_ai",
  startBooking: "start_booking",
  completePayment: "complete_payment",
  downloadPdf: "download_pdf",
  openMatchmaking: "open_matchmaking",
} as const;

export type EventName = (typeof EVENTS)[keyof typeof EVENTS];

const REDACT_KEYS = new Set([
  "birthTime",
  "birth_time",
  "tob",
  "timeOfBirth",
  "latitude",
  "longitude",
  "lat",
  "lng",
  "lon",
  "placeOfBirth",
  "birthLocation",
  "address",
  "message",
  "messages",
  "chat",
  "chatContent",
  "transcript",
  "prompt",
  "response",
  "email",
  "phone",
  "password",
  "token",
]);

const REDACT_KEY_PATTERN = /(?:password|passcode|token|secret|api[_-]?key|private[_-]?key|connection[_-]?string|database[_-]?url|webhook[_-]?(?:key|secret)|authorization|cookie|email|phone|prompt|transcript|message|response|body|content|stack|birth[_-]?(?:date|time|location)|place[_-]?of[_-]?birth|address|(?:first|last|full|display|legal)[_-]?name)/i;

function redactSensitiveText(input: string): string {
  return input
    .replace(/-----BEGIN (?:[A-Z0-9 ]+ )?PRIVATE KEY-----[\s\S]*?-----END (?:[A-Z0-9 ]+ )?PRIVATE KEY-----/g, "[redacted-private-key]")
    .replace(/\b(?:postgres(?:ql)?|mysql|mongodb(?:\+srv)?|redis):\/\/[^\s"'<>]+/gi, "[redacted-connection-string]")
    .replace(/\bBearer\s+[A-Za-z0-9._~+/=-]+/gi, "Bearer [redacted]")
    .replace(/\bBasic\s+[A-Za-z0-9._~+/=-]+/gi, "Basic [redacted]")
    .replace(/["']?(?:password|passcode|secret|api[_-]?key|access[_-]?token|refresh[_-]?token|session(?:[_-]?id)?|verification[_-]?token|reset[_-]?token|oauth[_-]?token|client[_-]?secret|service[_-]?role[_-]?key|private[_-]?key|signing[_-]?key|webhook[_-]?(?:key|secret)|database[_-]?url|db[_-]?url|connection[_-]?string|credential|token|authorization|cookie)["']?\s*[:=]\s*(?:"[^"]*"|'[^']*'|[^\s,;}\]]+)/gi, "[redacted]")
    .replace(/\b(?:sk|pk|rk|rzp|whsec|ghp|github_pat|xox[baprs]-|AIza|ya29\.|SG\.)[A-Za-z0-9._~+/=-]{8,}\b/gi, "[redacted-credential]")
    .replace(/\beyJ[A-Za-z0-9_-]{10,}\.[A-Za-z0-9_-]{10,}\.[A-Za-z0-9_-]{10,}\b/g, "[redacted-jwt]")
    .replace(/([?&](?:token|access_token|refresh_token|secret|key|signature|sig|jwt|x-amz-[^=]+|x-goog-[^=]+)=)[^&#\s]+/gi, "$1[redacted]")
    .replace(/https?:\/\/[^\s<>"')\]]+/gi, (url) =>
      /(?:storage\.googleapis\.com|amazonaws\.com|cloudfront\.net)\//i.test(url)
        ? "[redacted-signed-url]"
        : url)
    .replace(/\b[A-Z]{5}[0-9]{4}[A-Z]\b/gi, "[redacted-pan]")
    .replace(/\b\d{3}-\d{2}-\d{4}\b/g, "[redacted-ssn]")
    .replace(/\b\d{4}[\s-]?\d{4}[\s-]?\d{4}\b/g, "[redacted-id]")
    .replace(/\b(?:user|account|member|owner|customer|booking|session|client)[_-]?id\s*[:=]\s*[A-Za-z0-9._-]+/gi, "[redacted-user-id]")
    .replace(/\b(?:dob|date[_ -]?of[_ -]?birth|birth[_ -]?(?:date|time|place|location))\s*[:=]\s*[^,;\n]+/gi, "[redacted-birth-data]")
    .replace(/\b[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\b/gi, "[redacted]")
    .replace(/\b(?:\+?\d[\d().\s-]{8,}\d)\b/g, "[redacted-phone]");
}

export function sanitize<T>(input: T): T {
  if (input == null) return input;
  if (Array.isArray(input)) return input.map((v) => sanitize(v)) as unknown as T;
  if (input instanceof Error) {
    return { name: "Error", message: "Internal error" } as T;
  }
  if (typeof input === "object") {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(input as Record<string, unknown>)) {
      const normalized = k.replace(/([a-z0-9])([A-Z])/g, "$1_$2").toLowerCase();
      const compact = normalized.replace(/_/g, "");
      out[k] = REDACT_KEYS.has(k) || REDACT_KEY_PATTERN.test(normalized)
        || /^(?:name|username|userid|accountid|birthdate|dateofbirth|dob)$/.test(compact)
        ? "[redacted]"
        : sanitize(v);
    }
    return out as T;
  }
  if (typeof input === "string") return redactSensitiveText(input) as T;
  return input;
}

const OPT_OUT_KEY = "analytics-opt-out";

export function isOptedOutSync(): boolean {
  return isOptedOut();
}

export function isOptedOut(): boolean {
  if (typeof window === "undefined") return false;
  return window.localStorage.getItem(OPT_OUT_KEY) === "1";
}

export function setOptedOut(value: boolean): void {
  if (typeof window === "undefined") return;
  if (value) {
    window.localStorage.setItem(OPT_OUT_KEY, "1");
    try {
      posthog.opt_out_capturing();
    } catch {
      /* posthog may be uninitialised */
    }
  } else {
    window.localStorage.removeItem(OPT_OUT_KEY);
    try {
      posthog.opt_in_capturing();
    } catch {
      /* posthog may be uninitialised */
    }
  }
}

let posthogReady = false;
let sentryReady = false;

function sanitizeClientTelemetryUrl(value: string): string {
  try {
    const url = new URL(value);
    const path = url.pathname
      .replace(/\/[0-9a-f]{8}-[0-9a-f-]{27,}/gi, "/:id")
      .replace(/\/\d{4}-\d{2}-\d{2}(?=\/|$)/g, "/:date")
      .replace(/\/\d+(?=\/|$)/g, "/:id");
    return `${url.origin}${redactSensitiveText(path)}`;
  } catch {
    return redactSensitiveText(value.split(/[?#]/, 1)[0]);
  }
}

export function initTelemetry(): void {
  const posthogKey = import.meta.env["VITE_PUBLIC_POSTHOG_KEY"] as string | undefined;
  const posthogHost =
    (import.meta.env["VITE_PUBLIC_POSTHOG_HOST"] as string | undefined) ||
    "https://us.i.posthog.com";
  if (posthogKey) {
    posthog.init(posthogKey, {
      api_host: posthogHost,
      capture_pageview: false,
      autocapture: false,
      persistence: "localStorage",
      sanitize_properties: (props) => sanitize(props ?? {}),
      opt_out_capturing_by_default: isOptedOut(),
      loaded: () => {
        posthogReady = true;
      },
    });
  }

  const sentryDsn = import.meta.env["VITE_PUBLIC_SENTRY_DSN"] as string | undefined;
  if (sentryDsn) {
    Sentry.init({
      dsn: sentryDsn,
      environment: import.meta.env.MODE,
      tracesSampleRate: 0,
      sendDefaultPii: false,
      beforeSend(event) {
        if (event.request) {
          delete event.request.data;
          delete event.request.cookies;
          if (event.request.headers) {
            for (const key of Object.keys(event.request.headers)) {
              if (REDACT_KEY_PATTERN.test(key)) delete event.request.headers[key];
            }
          }
          if (event.request.url) event.request.url = sanitizeClientTelemetryUrl(event.request.url);
        }
        delete event.user;
        if (event.extra) event.extra = sanitize(event.extra);
        if (event.contexts) event.contexts = sanitize(event.contexts);
        if (event.message) event.message = "Application error";
        if (event.transaction) event.transaction = redactSensitiveText(event.transaction).slice(0, 200);
        if (event.exception?.values) {
          event.exception.values = event.exception.values.map((value) => {
            const safeValue = { ...value, value: "Internal error" };
            if (safeValue.stacktrace?.frames) {
              safeValue.stacktrace.frames = safeValue.stacktrace.frames.map((frame) => {
                const safeFrame = { ...frame } as typeof frame & Record<string, unknown>;
                delete safeFrame.vars;
                delete safeFrame.context_line;
                delete safeFrame.pre_context;
                delete safeFrame.post_context;
                return safeFrame;
              });
            }
            return safeValue;
          });
        }
        return event;
      },
    });
    sentryReady = true;
  }
}

export function track(event: EventName | string, properties: Record<string, unknown> = {}): void {
  if (!posthogReady || isOptedOut()) return;
  try {
    posthog.capture(event, sanitize(properties));
  } catch {
    /* swallow analytics failures */
  }
}

export function identify(distinctId: string, traits: Record<string, unknown> = {}): void {
  if (!posthogReady || isOptedOut()) return;
  try {
    posthog.identify(distinctId, sanitize(traits));
  } catch {
    /* ignore */
  }
}

export function resetAnalyticsIdentity(): void {
  if (!posthogReady) return;
  try {
    posthog.reset();
  } catch {
    /* ignore */
  }
}

export function captureException(err: unknown, extras: Record<string, unknown> = {}): void {
  const safeError = sanitize(err) as { name?: string; message?: string };
  const safeException = new Error(`${safeError.name ?? "Error"}: ${safeError.message ?? "Internal error"}`);
  if (!sentryReady) {
    // eslint-disable-next-line no-console
    console.error("[telemetry]", safeException.message, sanitize(extras));
    return;
  }
  Sentry.captureException(safeException, { extra: sanitize(extras) });
}

export const SentryErrorBoundary = Sentry.ErrorBoundary;
